import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../services/API";

const BASE = "/parental-controls";
const UNLOCK_STORAGE_KEY = "parentalControlUnlock";

const readStoredUnlock = () => {
    try {
        const raw = localStorage.getItem(UNLOCK_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed?.token || !parsed?.expiresAt || parsed.expiresAt <= Date.now()) {
            localStorage.removeItem(UNLOCK_STORAGE_KEY);
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
};

const writeStoredUnlock = (token, expiresAt) => {
    localStorage.setItem(UNLOCK_STORAGE_KEY, JSON.stringify({ token, expiresAt }));
};

const clearStoredUnlock = () => {
    localStorage.removeItem(UNLOCK_STORAGE_KEY);
};

const toRejection = (error) => {
    if (error.response) {
        return { ...error.response.data, status: error.response.status };
    }
    return { message: error.message, status: null };
};

export const getErrorMessage = (payload, fallback = "Something went wrong. Please try again.") =>
    payload?.message || payload?.error || payload?.detail || (typeof payload === "string" ? payload : fallback);

// Browsers null out the request body on GET/HEAD before sending (XHR spec), so the
// unlock_token/parent_pin credential has to travel as a header on these read endpoints instead.
const credentialHeaders = (credential = {}) => {
    const headers = {};
    if (credential.unlock_token) headers["X-Unlock-Token"] = credential.unlock_token;
    if (credential.parent_pin) headers["X-Parent-Pin"] = credential.parent_pin;
    return headers;
};

// 3.1
export const fetchParentalStatus = createAsyncThunk(
    "fetchParentalStatus",
    async (credential = {}, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(`${BASE}/status/`, { headers: credentialHeaders(credential) });
            return response.data;
        } catch (error) {
            return rejectWithValue(toRejection(error));
        }
    }
);

// 3.2
export const fetchLinkedParentEmail = createAsyncThunk(
    "fetchLinkedParentEmail",
    async (credential = {}, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(`${BASE}/parent-email/linked/`, { headers: credentialHeaders(credential) });
            return response.data;
        } catch (error) {
            return rejectWithValue(toRejection(error));
        }
    }
);

// 3.3
export const sendParentEmail = createAsyncThunk(
    "sendParentEmail",
    async (data, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(`${BASE}/parent-email/`, data);
            return response.data;
        } catch (error) {
            return rejectWithValue(toRejection(error));
        }
    }
);

// 3.4
export const verifyParentEmail = createAsyncThunk(
    "verifyParentEmail",
    async (data, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(`${BASE}/parent-email/verify/`, data);
            return response.data;
        } catch (error) {
            return rejectWithValue(toRejection(error));
        }
    }
);

// 3.5
export const createParentalPin = createAsyncThunk(
    "createParentalPin",
    async (data, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(`${BASE}/pin/`, data);
            return response.data;
        } catch (error) {
            return rejectWithValue(toRejection(error));
        }
    }
);

// 3.6
export const unlockParentalPin = createAsyncThunk(
    "unlockParentalPin",
    async (data, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(`${BASE}/unlock/`, data);
            return response.data;
        } catch (error) {
            return rejectWithValue(toRejection(error));
        }
    }
);

// 3.7
export const updateParentalSettings = createAsyncThunk(
    "updateParentalSettings",
    async (data, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.patch(`${BASE}/settings/`, data);
            return response.data;
        } catch (error) {
            return rejectWithValue(toRejection(error));
        }
    }
);

// 3.8
export const requestPinReset = createAsyncThunk(
    "requestPinReset",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(`${BASE}/pin/reset/request/`);
            return response.data;
        } catch (error) {
            return rejectWithValue(toRejection(error));
        }
    }
);

// 3.9
export const verifyPinReset = createAsyncThunk(
    "verifyPinReset",
    async (data, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(`${BASE}/pin/reset/verify/`, data);
            return response.data;
        } catch (error) {
            return rejectWithValue(toRejection(error));
        }
    }
);

// 3.10
export const confirmPinReset = createAsyncThunk(
    "confirmPinReset",
    async (data, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(`${BASE}/pin/reset/confirm/`, data);
            return response.data;
        } catch (error) {
            return rejectWithValue(toRejection(error));
        }
    }
);

// 3.11
export const fetchUsageToday = createAsyncThunk(
    "fetchUsageToday",
    async (credential = {}, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(`${BASE}/usage/today/`, { headers: credentialHeaders(credential) });
            return response.data;
        } catch (error) {
            return rejectWithValue(toRejection(error));
        }
    }
);

// 3.12
export const fetchAuditLog = createAsyncThunk(
    "fetchAuditLog",
    async (credential = {}, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(`${BASE}/audit-log/`, { headers: credentialHeaders(credential) });
            return response.data;
        } catch (error) {
            return rejectWithValue(toRejection(error));
        }
    }
);

const isPinLockError = (payload) => payload?.status === 423;

const storedUnlock = readStoredUnlock();

const initialState = {
    status: null,
    statusLoading: false,
    statusError: null,

    linkedEmail: null,
    linkedEmailLoading: false,
    linkedEmailError: null,

    sendEmailLoading: false,
    sendEmailError: null,

    verifyEmailLoading: false,
    verifyEmailError: null,

    createPinLoading: false,
    createPinError: null,

    unlockToken: storedUnlock?.token ?? null,
    unlockTokenExpiresAt: storedUnlock?.expiresAt ?? null,
    unlockLoading: false,
    unlockError: null,

    updateSettingsLoading: false,
    updateSettingsError: null,

    pinResetRequestLoading: false,
    pinResetRequestError: null,

    pinResetVerifyLoading: false,
    pinResetVerifyError: null,

    pinResetConfirmLoading: false,
    pinResetConfirmError: null,

    usageToday: null,
    usageLoading: false,
    usageError: null,

    auditLog: [],
    auditLogLoading: false,
    auditLogError: null,

    pinLocked: false,
    pinLockMessage: null,
};

export const ParentalControl = createSlice({
    name: "ParentalControl",
    initialState,
    reducers: {
        clearUnlockToken: (state) => {
            state.unlockToken = null;
            state.unlockTokenExpiresAt = null;
            clearStoredUnlock();
        },
        resetParentalControlErrors: (state) => {
            state.statusError = null;
            state.sendEmailError = null;
            state.verifyEmailError = null;
            state.createPinError = null;
            state.unlockError = null;
            state.updateSettingsError = null;
            state.pinResetRequestError = null;
            state.pinResetVerifyError = null;
            state.pinResetConfirmError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // status
            .addCase(fetchParentalStatus.pending, (state) => {
                state.statusLoading = true;
                state.statusError = null;
            })
            .addCase(fetchParentalStatus.fulfilled, (state, action) => {
                state.statusLoading = false;
                state.status = action.payload;
            })
            .addCase(fetchParentalStatus.rejected, (state, action) => {
                state.statusLoading = false;
                state.statusError = action.payload;
            })

            // linked parent email
            .addCase(fetchLinkedParentEmail.pending, (state) => {
                state.linkedEmailLoading = true;
                state.linkedEmailError = null;
            })
            .addCase(fetchLinkedParentEmail.fulfilled, (state, action) => {
                state.linkedEmailLoading = false;
                state.linkedEmail = action.payload?.parent_email ?? null;
            })
            .addCase(fetchLinkedParentEmail.rejected, (state, action) => {
                state.linkedEmailLoading = false;
                state.linkedEmailError = action.payload;
            })

            // send parent email
            .addCase(sendParentEmail.pending, (state) => {
                state.sendEmailLoading = true;
                state.sendEmailError = null;
            })
            .addCase(sendParentEmail.fulfilled, (state) => {
                state.sendEmailLoading = false;
            })
            .addCase(sendParentEmail.rejected, (state, action) => {
                state.sendEmailLoading = false;
                state.sendEmailError = action.payload;
            })

            // verify parent email otp
            .addCase(verifyParentEmail.pending, (state) => {
                state.verifyEmailLoading = true;
                state.verifyEmailError = null;
            })
            .addCase(verifyParentEmail.fulfilled, (state) => {
                state.verifyEmailLoading = false;
            })
            .addCase(verifyParentEmail.rejected, (state, action) => {
                state.verifyEmailLoading = false;
                state.verifyEmailError = action.payload;
            })

            // create pin
            .addCase(createParentalPin.pending, (state) => {
                state.createPinLoading = true;
                state.createPinError = null;
            })
            .addCase(createParentalPin.fulfilled, (state) => {
                state.createPinLoading = false;
            })
            .addCase(createParentalPin.rejected, (state, action) => {
                state.createPinLoading = false;
                state.createPinError = action.payload;
            })

            // unlock
            .addCase(unlockParentalPin.pending, (state) => {
                state.unlockLoading = true;
                state.unlockError = null;
            })
            .addCase(unlockParentalPin.fulfilled, (state, action) => {
                state.unlockLoading = false;
                state.unlockToken = action.payload?.unlock_token ?? null;
                state.unlockTokenExpiresAt = action.payload?.expires_in_minutes
                    ? Date.now() + action.payload.expires_in_minutes * 60 * 1000
                    : null;
                state.pinLocked = false;
                state.pinLockMessage = null;
                if (state.unlockToken && state.unlockTokenExpiresAt) {
                    writeStoredUnlock(state.unlockToken, state.unlockTokenExpiresAt);
                } else {
                    clearStoredUnlock();
                }
            })
            .addCase(unlockParentalPin.rejected, (state, action) => {
                state.unlockLoading = false;
                state.unlockError = action.payload;
                if (isPinLockError(action.payload)) {
                    state.pinLocked = true;
                    state.pinLockMessage = getErrorMessage(action.payload);
                }
            })

            // update settings
            .addCase(updateParentalSettings.pending, (state) => {
                state.updateSettingsLoading = true;
                state.updateSettingsError = null;
            })
            .addCase(updateParentalSettings.fulfilled, (state, action) => {
                state.updateSettingsLoading = false;
                state.status = action.payload;
            })
            .addCase(updateParentalSettings.rejected, (state, action) => {
                state.updateSettingsLoading = false;
                state.updateSettingsError = action.payload;
                if (isPinLockError(action.payload)) {
                    state.pinLocked = true;
                    state.pinLockMessage = getErrorMessage(action.payload);
                }
            })

            // pin reset request
            .addCase(requestPinReset.pending, (state) => {
                state.pinResetRequestLoading = true;
                state.pinResetRequestError = null;
            })
            .addCase(requestPinReset.fulfilled, (state) => {
                state.pinResetRequestLoading = false;
            })
            .addCase(requestPinReset.rejected, (state, action) => {
                state.pinResetRequestLoading = false;
                state.pinResetRequestError = action.payload;
            })

            // pin reset verify
            .addCase(verifyPinReset.pending, (state) => {
                state.pinResetVerifyLoading = true;
                state.pinResetVerifyError = null;
            })
            .addCase(verifyPinReset.fulfilled, (state) => {
                state.pinResetVerifyLoading = false;
            })
            .addCase(verifyPinReset.rejected, (state, action) => {
                state.pinResetVerifyLoading = false;
                state.pinResetVerifyError = action.payload;
            })

            // pin reset confirm
            .addCase(confirmPinReset.pending, (state) => {
                state.pinResetConfirmLoading = true;
                state.pinResetConfirmError = null;
            })
            .addCase(confirmPinReset.fulfilled, (state) => {
                state.pinResetConfirmLoading = false;
                state.pinLocked = false;
                state.pinLockMessage = null;
            })
            .addCase(confirmPinReset.rejected, (state, action) => {
                state.pinResetConfirmLoading = false;
                state.pinResetConfirmError = action.payload;
            })

            // usage today
            .addCase(fetchUsageToday.pending, (state) => {
                state.usageLoading = true;
                state.usageError = null;
            })
            .addCase(fetchUsageToday.fulfilled, (state, action) => {
                state.usageLoading = false;
                state.usageToday = action.payload;
            })
            .addCase(fetchUsageToday.rejected, (state, action) => {
                state.usageLoading = false;
                state.usageError = action.payload;
            })

            // audit log
            .addCase(fetchAuditLog.pending, (state) => {
                state.auditLogLoading = true;
                state.auditLogError = null;
            })
            .addCase(fetchAuditLog.fulfilled, (state, action) => {
                state.auditLogLoading = false;
                state.auditLog = Array.isArray(action.payload) ? action.payload : [];
            })
            .addCase(fetchAuditLog.rejected, (state, action) => {
                state.auditLogLoading = false;
                state.auditLogError = action.payload;
            });
    },
});

export const { clearUnlockToken, resetParentalControlErrors } = ParentalControl.actions;
export default ParentalControl.reducer;
